/* GASSISA
=========================================================================================
* Descripción: Modal con el detalle cruzado de chequeos por día de un empleado
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 07/08/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 10/08/2026 | Lic. José Luis Aragón Cervantes  | Agrega vista previa de recibo de nómina
* ---------------------------------------------------------------------------------------
*/

import { Box, Button, Chip, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";

import SizeSoftXModal from "../leyout/SizeSoftXModal";

const CONCEPTOS = [
    { campo: "entrada", titulo: "Entrada" },
    { campo: "salidaComer", titulo: "Salida a comer" },
    { campo: "regresoComida", titulo: "Regreso comida" },
    { campo: "salida", titulo: "Salida" },
];

const formatHora = (hora) => {
    if (!hora) return "";
    const partes = String(hora).split(":");
    return partes.length >= 2 ? `${partes[0]}:${partes[1]}` : hora;
};

const formatFechaCorta = (fechaKey) => {
    if (!fechaKey) return "";
    const partes = String(fechaKey).split("-");
    if (partes.length !== 3) return fechaKey;
    return `${partes[2]}/${partes[1]}`;
};

export default function ModalDetalleChequeosNominas({ open, onClose, fila, onImprimirTicket, onVistaPreviaTicket }) {
    const chequeos = (fila && fila.chequeosPorDia) || [];
    const totalHoras = chequeos.reduce((suma, dia) => suma + (dia.horas || 0), 0);
    const totalRetardos = chequeos.reduce((suma, dia) => suma + (dia.retardo ? 1 : 0), 0);
    const diasTrabajados = chequeos.filter((dia) => dia.trabajo).length;

    const renderHoraCelda = (dia, campo) => {
        const valor = dia?.[campo];
        if (!valor) return <span style={{ color: "#bbb" }}>-</span>;
        const inyectado = (dia.inyectados || []).includes(campo);
        const esRetardo = campo === "entrada" && dia.retardo;

        return (
            <span style={{ color: esRetardo ? "#d32f2f" : inyectado ? "#ed6c02" : "inherit", fontWeight: inyectado ? 600 : 400 }}>
                {formatHora(valor)}
                {inyectado ? " *" : ""}
            </span>
        );
    };

    return (
        <SizeSoftXModal
            open={open}
            onClose={onClose}
            onSuccess={onClose}
            success="Cerrar"
            hideCancel
            maxWidth="xl"
            title={`Detalle de chequeos — ${fila?.nombre || fila?.usuario || ""}`}
        >
            <Stack spacing={1.5}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <Chip label={`Usuario: ${fila?.usuario || "-"}`} size="small" variant="outlined" />
                    <Chip label={`Días trabajados: ${diasTrabajados}`} size="small" variant="outlined" />
                    <Chip label={`Horas totales: ${totalHoras.toFixed(2)}`} size="small" variant="outlined" />
                    <Chip label={`Retardos: ${totalRetardos}`} size="small" variant="outlined" color={totalRetardos > 0 ? "error" : "default"} />
                </Stack>

                <TableContainer sx={{ maxHeight: "52vh", overflow: "auto" }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Concepto</TableCell>
                                {chequeos.map((dia) => (
                                    <TableCell key={dia.fecha} align="center" sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                                        {formatFechaCorta(dia.fecha)}
                                        {dia.retardo ? " ⚠" : ""}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {CONCEPTOS.map((concepto) => (
                                <TableRow key={concepto.campo}>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{concepto.titulo}</TableCell>
                                    {chequeos.map((dia) => (
                                        <TableCell key={dia.fecha} align="center">
                                            {renderHoraCelda(dia, concepto.campo)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>Horas</TableCell>
                                {chequeos.map((dia) => (
                                    <TableCell key={dia.fecha} align="center">
                                        {dia.horas ? dia.horas.toFixed(2) : "-"}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                <Typography variant="caption" color="text.secondary">
                    Los tiempos marcados con * fueron completados automáticamente por el sistema (salida 20:00 o regreso de comida con 1 hora).
                </Typography>

                {(onVistaPreviaTicket || onImprimirTicket) && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                        {onVistaPreviaTicket && (
                            <Button
                                type="button"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => onVistaPreviaTicket(fila)}
                            >
                                Vista previa
                            </Button>
                        )}
                        {onImprimirTicket && (
                            <Button
                                type="button"
                                variant="contained"
                                startIcon={<PrintIcon />}
                                onClick={() => onImprimirTicket(fila)}
                            >
                                Imprimir recibo
                            </Button>
                        )}
                    </Box>
                )}
            </Stack>
        </SizeSoftXModal>
    );
}